import { axiosPostData } from "./fetch_services";
import { waitForFileProcessing } from "./fileProcessing";
import {
  appendFilesToFormData,
  buildMetaData,
  calculateSurveyTotal,
  processAnswers,
} from "./surveyHelper";

/** Clone Redux answers so partial-save processing cannot mutate live state. */
const cloneAnswersSnapshot = (answers) => {
  if (!answers || typeof answers !== "object") {
    return {};
  }
  return Object.fromEntries(
    Object.entries(answers).map(([questionId, answer]) => [
      questionId,
      answer && typeof answer === "object" ? { ...answer } : answer,
    ]),
  );
};

/**
 * Build FormData and submit a partial survey response
 * @returns {Promise<Object>} - { success, data? }
 */
const submitPartialResponse = async ({
  allQuestions,
  questionsToSave,
  getHiddenQuestionIds,
  store,
  contactDetails,
  customValues,
  contactFormData,
  guest_info,
  visitDate,
  visitTimeIn,
  selectedTimeOut,
  survey_without_login,
  showContactForm,
  directorMessage,
}) => {
  if (!survey_without_login?.allowPartialResponse) {
    return { success: true };
  }

  await waitForFileProcessing();

  let responseData = {
    customer_name: contactDetails?.name,
    customer_email: contactDetails?.email,
    customer_phone: contactDetails?.phone,
  };

  const answeresCopy = cloneAnswersSnapshot(store.getState().survey.answeres);
  const { processedAnswers, filesArray } = processAnswers(
    answeresCopy,
    questionsToSave,
  );

  const surveyTotal = calculateSurveyTotal(allQuestions);

  const hiddenQuestionIds = await getHiddenQuestionIds();
  hiddenQuestionIds?.length > 0 &&
    hiddenQuestionIds?.forEach((questionId) => {
      delete answeresCopy[questionId];
    });

  const metaData = buildMetaData({
    customValues,
    contactFormData,
    guest_info,
    visitDate,
    visitTimeIn,
    selectedTimeOut,
    survey_without_login,
    showContactForm,
  });

  const callcenter = localStorage.getItem("callcenter") ?? null;
  if (callcenter === "true") {
    responseData.call_center = true;
  }

  const ticketCreatorId = localStorage.getItem("creator_id") ?? null;
  const source = localStorage.getItem("source") ?? null;

  const form_data = new FormData();
  form_data.append("metaData", JSON.stringify(metaData));
  form_data.append("jsonData", JSON.stringify(processedAnswers || {}));
  form_data.append("responseData", JSON.stringify(responseData));
  if (directorMessage) {
    form_data.append("director_message", directorMessage);
  }
  form_data.append("ticketCreatorId", ticketCreatorId);
  form_data.append("source", source);
  form_data.append("surveyTotal", surveyTotal);
  const companyId = localStorage.getItem("company_id");
  form_data.append("company_id", companyId);
  if (
    localStorage.getItem("tmp_response_id") &&
    localStorage.getItem("tmp_response_id") !== "undefined"
  ) {
    form_data.append(
      "tmp_response_id",
      localStorage.getItem("tmp_response_id"),
    );
  }

  appendFilesToFormData(form_data, filesArray);

  try {
    const result = await axiosPostData("tmp-response/save", form_data);

    if (result) {
      const tmpResponseId = result?.data?.data?._id;
      if (tmpResponseId) {
        localStorage.setItem("tmp_response_id", tmpResponseId);
      }
      return { success: true, data: result.data };
    }
    return { success: false };
  } catch (e) {
    console.log(e);
    return { success: false, error: e };
  }
};

const hasAnswersForQuestions = (questions, answeresCopy) =>
  questions?.some((question) => answeresCopy[question._id]);

/**
 * Save partial answers for current page
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} - { success, data?, error? }
 */
const savePartialResponse = async (params) => {
  const { allQuestions, trimStart, trimEnd, store, ...submitParams } = params;

  try {
    const currentPageQuestions = allQuestions?.slice(trimStart, trimEnd + 1);
    if (!currentPageQuestions?.length) {
      return { success: true };
    }

    const answeresCopy = store.getState().survey.answeres;
    if (!hasAnswersForQuestions(currentPageQuestions, answeresCopy)) {
      return { success: true };
    }

    return await submitPartialResponse({
      allQuestions,
      questionsToSave: currentPageQuestions,
      store,
      ...submitParams,
    });
  } catch (error) {
    console.log(error);
  }
};

/**
 * Save partial answers for current page for mobile
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} - { success, data?, error? }
 */
const savePartialResponseForMobile = async (params) => {
  const { allQuestions, currentQuestionIndex, store, ...submitParams } = params;

  try {
    const answeredQuestions = allQuestions.slice(0, currentQuestionIndex + 1);
    const answeresCopy = store.getState().survey.answeres;

    if (!hasAnswersForQuestions(answeredQuestions, answeresCopy)) {
      return { success: true };
    }

    return await submitPartialResponse({
      allQuestions,
      questionsToSave: answeredQuestions,
      store,
      ...submitParams,
    });
  } catch (error) {
    console.log(error);
  }
};

/**
 * Save all answers and content when the user exits the survey.
 * Always calls tmp-response/save, even with empty answers (no validation).
 * @param {Object} params - Parameters object
 * @returns {Promise<Object>} - { success, data?, error? }
 */
const savePartialResponseOnExit = async (params) => {
  const { allQuestions, store, ...submitParams } = params;

  try {
    const answeresCopy = store.getState().survey.answeres;
    const questionsToSave = allQuestions?.filter(
      (question) => answeresCopy[question._id],
    );

    return await submitPartialResponse({
      allQuestions,
      questionsToSave: questionsToSave || [],
      store,
      ...submitParams,
    });
  } catch (error) {
    console.log(error);
    return { success: false, error };
  }
};

export {
  savePartialResponse,
  savePartialResponseForMobile,
  savePartialResponseOnExit,
};
